/**
 * Settings ▸ Members — members table, pending invites, invite composer
 * (#494). Backed by the shared-auth member-management API (#121) and
 * invites API (#119). Role-gated: viewers/editors see the member list
 * read-only; the invites section is admin+ only (the API 403s it
 * anyway). "Resend" is revoke + recreate — the server mints a fresh
 * token and sends a new email.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, FileDown, Mail, Plus, RefreshCw, Trash2, UserMinus, X } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  errorStatus,
  problemDetail,
  tenantApi,
  type PendingInvite,
  type TenantBinding,
  type TenantMember,
} from '@/services/tenantApi';
import { spacesApi, type Space } from '@/services/spacesApi';
import { presetBinding, presetFromBindings, presetLabel, toWriteBinding } from '@/lib/grants';
import { usePresets } from '@/lib/usePresets';
import { cn } from '@/lib/utils';

type InviteRole = 'admin' | 'editor' | 'viewer';

const ROLE_OPTIONS: InviteRole[] = ['admin', 'editor', 'viewer'];

// The default preset for new invites: Capture + Build + read Document (D51),
// matching the server-side default when bindings are omitted.
const DEFAULT_INVITE_PRESET = 'product-owner';

// The composer's access picker: a catalogue preset slug on the open baseline,
// or 'guest' — an external reader locked to one space (viewer rank, docs:read
// only there; the server enforces both).
type InviteAccess = string;

const selectClass =
  'rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-slate-700 dark:text-slate-300 focus:border-purple-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';

function initial(member: TenantMember): string {
  return (member.name || member.email || '?').charAt(0).toUpperCase();
}

export default function MembersTab() {
  const { tenant, refreshTenants } = useTenant();
  const { user } = useAuthContext();

  const canManage = tenant?.role === 'owner' || tenant?.role === 'admin';
  const isOwner = tenant?.role === 'owner';
  const selfUserId = useMemo(() => {
    const id = user?.userId ?? '';
    return id && !id.startsWith('usr_') ? `usr_${id}` : id;
  }, [user?.userId]);

  const [members, setMembers] = useState<TenantMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenant) return;
    try {
      const jobs: Promise<void>[] = [
        tenantApi.listMembers(tenant.tenantId).then(setMembers),
      ];
      if (canManage) {
        jobs.push(tenantApi.listInvites(tenant.tenantId).then(setInvites));
      }
      await Promise.all(jobs);
      setError(null);
    } catch (err) {
      setError(problemDetail(err, 'Could not load members.'));
    } finally {
      setLoading(false);
    }
  }, [tenant, canManage]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const changeRole = async (member: TenantMember, role: string) => {
    if (!tenant || busyId) return;
    setBusyId(member.userId);
    setError(null);
    try {
      await tenantApi.changeMemberRole(tenant.tenantId, member.userId, role);
      setMembers((prev) =>
        prev.map((m) => (m.userId === member.userId ? { ...m, role } : m)),
      );
    } catch (err) {
      // 409 last-owner and 403 owner-boundary come back as problem details.
      setError(problemDetail(err, 'Could not change the role.'));
    } finally {
      setBusyId(null);
    }
  };

  // The catalogue behind every picker here. Null while loading or after a
  // failed read — the pickers are then disabled and `presetsError` shown;
  // there is deliberately no built-in list to fall back to.
  const { presets, error: presetsError } = usePresets();

  const changePersona = async (member: TenantMember, slug: string) => {
    if (!tenant || busyId || !presets) return;
    setBusyId(member.userId);
    setError(null);
    try {
      // One preset on the open baseline, spelt out as the scopes it names —
      // the server stores the scopes and checks the label against them.
      const bindings = [presetBinding('*', slug, presets)];
      await tenantApi.setMemberBindings(tenant.tenantId, member.userId, bindings);
      setMembers((prev) =>
        prev.map((m) => (m.userId === member.userId ? { ...m, bindings } : m)),
      );
    } catch (err) {
      // 403 binding_exceeds_grantor names the scopes the caller lacks there.
      setError(problemDetail(err, 'Could not change the access preset.'));
    } finally {
      setBusyId(null);
    }
  };

  const removeMember = async (member: TenantMember) => {
    if (!tenant || busyId) return;
    setBusyId(member.userId);
    setError(null);
    try {
      await tenantApi.removeMember(tenant.tenantId, member.userId);
      setMembers((prev) => prev.filter((m) => m.userId !== member.userId));
      setConfirmRemove(null);
      if (member.userId === selfUserId) {
        await refreshTenants();
        window.location.assign('/');
      }
    } catch (err) {
      setError(problemDetail(err, 'Could not remove the member.'));
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvite = async (invite: PendingInvite) => {
    if (!tenant || busyId) return;
    setBusyId(invite.inviteId);
    setError(null);
    try {
      await tenantApi.revokeInvite(tenant.tenantId, invite.inviteId);
      setInvites((prev) => prev.filter((i) => i.inviteId !== invite.inviteId));
    } catch (err) {
      setError(problemDetail(err, 'Could not revoke the invite.'));
    } finally {
      setBusyId(null);
    }
  };

  const resendInvite = async (invite: PendingInvite) => {
    if (!tenant || busyId || !presets) return;
    setBusyId(invite.inviteId);
    setError(null);
    // Bindings carry over so the access survives the resend. A binding the
    // write shape cannot express (no scopes and no catalogue preset) refuses
    // loudly rather than narrowing the invite — and before anything is sent.
    let bindings: TenantBinding[] | undefined;
    try {
      bindings = invite.bindings?.map((b) => toWriteBinding(b, presets));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusyId(null);
      return;
    }
    try {
      // No resend API: recreate, THEN revoke — in that order, so a create that
      // fails (a 4xx, a 5xx, a shape the server no longer accepts) leaves the
      // pending invite standing instead of destroying it with nothing in its
      // place.
      await tenantApi.createInvites(tenant.tenantId, [
        {
          email: invite.email,
          role: invite.role as InviteRole,
          bindings,
          memberClass: invite.memberClass,
        },
      ]);
      await tenantApi.revokeInvite(tenant.tenantId, invite.inviteId);
      const fresh = await tenantApi.listInvites(tenant.tenantId);
      setInvites(fresh);
    } catch (err) {
      setError(problemDetail(err, 'Could not resend the invite.'));
    } finally {
      setBusyId(null);
    }
  };

  // ── Entitlement report export (#697) ───────────────────────────────
  const [exporting, setExporting] = useState(false);

  const downloadRecert = async () => {
    if (!tenant || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const snap = await tenantApi.getRecertSnapshot(tenant.tenantId);
      const blob = new Blob([JSON.stringify(snap, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `entitlements-${tenant.slug ?? tenant.tenantId}-${snap.generatedAt.slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(problemDetail(err, 'Could not generate the entitlement report.'));
    } finally {
      setExporting(false);
    }
  };

  // ── Invite composer ────────────────────────────────────────────────
  const [composer, setComposer] = useState<
    { email: string; role: InviteRole; persona: InviteAccess; space?: string }[]
  >([]);
  const [sending, setSending] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);

  // Spaces feed the guest-invite space picker; loaded once an admin opens
  // the composer (guests must be pinned to a specific space).
  const [spaces, setSpaces] = useState<Space[] | null>(null);
  useEffect(() => {
    if (!canManage || spaces !== null || composer.length === 0) return;
    spacesApi
      .listSpaces()
      .then(setSpaces)
      .catch(() => setSpaces([]));
  }, [canManage, spaces, composer.length]);

  const guestRowsIncomplete = composer.some(
    (r) => r.email.trim() && r.persona === 'guest' && !r.space,
  );

  const sendInvites = async () => {
    if (!tenant || sending || guestRowsIncomplete || !presets) return;
    let rows: Parameters<typeof tenantApi.createInvites>[1];
    try {
      rows = composer
        .map((r) =>
          r.persona === 'guest'
            ? {
                email: r.email.trim().toLowerCase(),
                // Guests are viewer-ranked external readers of ONE space; the
                // server rejects any other rank or a '*' binding. 'member' is
                // the Reader preset (docs:read).
                role: 'viewer' as const,
                memberClass: 'guest' as const,
                bindings: [presetBinding(r.space ?? '', 'member', presets)],
              }
            : {
                email: r.email.trim().toLowerCase(),
                role: r.role,
                bindings: [presetBinding('*', r.persona, presets)],
              },
        )
        .filter((r) => r.email);
    } catch (err) {
      // A row naming a preset the catalogue lacks: a picker bug, not a send.
      setComposerError(err instanceof Error ? err.message : String(err));
      return;
    }
    if (rows.length === 0) return;
    setSending(true);
    setComposerError(null);
    try {
      await tenantApi.createInvites(tenant.tenantId, rows);
      setComposer([]);
      setInvites(await tenantApi.listInvites(tenant.tenantId));
    } catch (err) {
      setComposerError(
        errorStatus(err) === 409
          ? problemDetail(err, 'A pending invite already exists for one of these emails.')
          : problemDetail(err, 'Could not send the invites.'),
      );
    } finally {
      setSending(false);
    }
  };

  if (!tenant) {
    return <LoadingState size="lg" label="Loading…" />;
  }

  return (
    <div className="space-y-8">
      {!canManage && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          You have <span className="font-medium">{tenant.role ?? 'viewer'}</span> access — the
          member list is read-only.
        </p>
      )}

      {error && (
        <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </p>
      )}
      {presetsError && (
        <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" /> Access presets unavailable — {presetsError}
        </p>
      )}

      {/* Members */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Members{!loading && ` (${members.length})`}
          </h2>
          {canManage && (
            <button
              type="button"
              onClick={downloadRecert}
              disabled={exporting || loading}
              title="Download the entitlement recertification report (JSON): every member's workspace role, class, bindings and resolved grants. Generating one is audited."
              className="flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              {exporting ? <Spinner size="sm" tone="current" /> : <FileDown className="w-3.5 h-3.5" />}
              Entitlement report
            </button>
          )}
        </div>
        {loading ? (
          <LoadingState size="md" label="Loading members…" />
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800">
            {members.map((member) => {
              const isSelf = member.userId === selfUserId;
              const memberIsOwner = member.role === 'owner';
              const memberIsGuest = member.memberClass === 'guest';
              // Admins cannot touch owners; the owner pick is owner-only.
              const canEditRow = canManage && !isSelf && (isOwner || !memberIsOwner);
              const busy = busyId === member.userId;
              // No bindings from the API yet — hide the control. The preset is
              // also null until the catalogue arrives: a row cannot be named
              // without it, and the picker is disabled meanwhile.
              const hasBindings = (member.bindings?.length ?? 0) > 0;
              const persona = presets ? presetFromBindings(member.bindings, presets) : null;
              const memberHasGroups = (member.groups?.length ?? 0) > 0;
              return (
                <div key={member.userId} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50 text-sm font-semibold text-purple-700 dark:text-purple-300">
                    {initial(member)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {member.name || member.email || member.userId}
                      {isSelf && <span className="ml-1.5 text-xs text-slate-400">(you)</span>}
                    </div>
                    {member.name && member.email && (
                      <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                        {member.email}
                      </div>
                    )}
                  </div>
                  {busy && <Spinner size="sm" />}
                  {memberIsGuest && (
                    // A guest's rank and wildcard access are locked server-side
                    // (guest_role_immutable / guest_wildcard_binding), so the
                    // persona and role editors are replaced by the badge.
                    <span
                      className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"
                      title="External guest — reads exactly the spaces named in their bindings"
                    >
                      Guest
                    </span>
                  )}
                  {!memberIsGuest && (hasBindings || memberHasGroups) &&
                    (canEditRow ? (
                      <select
                        value={persona ?? ''}
                        onChange={(e) => changePersona(member, e.target.value)}
                        disabled={Boolean(busyId) || !presets}
                        className={selectClass}
                        title="Space role — what this member can do with content (Capture / Build / Knowledge Base)"
                      >
                        {!presets && (
                          <option value="" disabled>
                            {presetsError ? 'Presets unavailable' : 'Loading presets…'}
                          </option>
                        )}
                        {persona === 'custom' && (
                          <option value="custom" disabled>
                            Custom ({member.bindings?.length ?? 0} bindings)
                          </option>
                        )}
                        {presets && persona === null && (
                          // Group-only member: no manual baseline yet. Picking
                          // one ADDS a manual binding; group access is untouched.
                          <option value="" disabled>
                            No baseline
                          </option>
                        )}
                        {(presets ?? []).map((p) => (
                          <option key={p.slug} value={p.slug} title={p.description}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        {persona === null
                          ? '—'
                          : persona === 'custom'
                            ? 'Custom'
                            : presetLabel(persona, presets ?? [])}
                      </span>
                    ))}
                  {!memberIsGuest && memberHasGroups && (
                    <span
                      className="rounded-full bg-purple-50 dark:bg-purple-950/50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300"
                      title={`Also inherits access from ${member.groups?.length} group${
                        (member.groups?.length ?? 0) === 1 ? '' : 's'
                      } (see Settings ▸ Groups): ${Object.keys(member.groupGrants ?? {})
                        .map((s) => (s === '*' ? 'all open spaces' : s))
                        .join(', ')}`}
                    >
                      +{member.groups?.length} group{(member.groups?.length ?? 0) === 1 ? '' : 's'}
                    </span>
                  )}
                  {canEditRow && !memberIsGuest ? (
                    <select
                      value={member.role}
                      onChange={(e) => changeRole(member, e.target.value)}
                      disabled={Boolean(busyId)}
                      className={selectClass}
                      title="Workspace role — what this member can administer"
                    >
                      {memberIsOwner || isOwner ? <option value="owner">Owner</option> : null}
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r} value={r}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      {member.role}
                    </span>
                  )}
                  {canEditRow &&
                    (confirmRemove === member.userId ? (
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => removeMember(member)}
                          disabled={Boolean(busyId)}
                          className="rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Remove
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmRemove(null)}
                          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRemove(member.userId)}
                        disabled={Boolean(busyId)}
                        title={`Remove ${member.name || member.email}`}
                        className="rounded-md p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Pending invites */}
      {canManage && invites.length > 0 && (
        <section>
          <h2 className="mb-3 text-base font-semibold text-slate-900 dark:text-slate-100">
            Pending invites ({invites.length})
          </h2>
          <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800">
            {invites.map((invite) => {
              const busy = busyId === invite.inviteId;
              return (
                <div key={invite.inviteId} className="flex items-center gap-3 px-4 py-3">
                  <Mail className="w-4 h-4 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {invite.email}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Expires {new Date(invite.expiresAt * 1000).toLocaleDateString()}
                    </div>
                  </div>
                  {invite.memberClass === 'guest' ? (
                    <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                      Guest
                    </span>
                  ) : (
                    (() => {
                      const invitePersona = presets
                        ? presetFromBindings(invite.bindings, presets)
                        : null;
                      return invitePersona ? (
                        <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {invitePersona === 'custom'
                            ? 'Custom'
                            : presetLabel(invitePersona, presets ?? [])}
                        </span>
                      ) : null;
                    })()
                  )}
                  <span className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    {invite.role}
                  </span>
                  {busy ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => resendInvite(invite)}
                        disabled={Boolean(busyId) || !presets}
                        title="Resend invite (new link)"
                        className="rounded-md p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => revokeInvite(invite)}
                        disabled={Boolean(busyId)}
                        title="Revoke invite"
                        className="rounded-md p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Invite composer */}
      {canManage && (
        <section>
          <h2 className="mb-3 text-base font-semibold text-slate-900 dark:text-slate-100">
            Invite people
          </h2>
          <div className="space-y-2">
            {composer.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={row.email}
                  onChange={(e) => {
                    const next = [...composer];
                    next[i] = { ...row, email: e.target.value };
                    setComposer(next);
                    setComposerError(null);
                  }}
                  type="email"
                  placeholder="teammate@example.com"
                  className="w-full max-w-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-purple-500 focus:outline-none"
                  disabled={sending}
                />
                <select
                  value={row.persona}
                  onChange={(e) => {
                    const next = [...composer];
                    next[i] = { ...row, persona: e.target.value as InviteAccess };
                    setComposer(next);
                  }}
                  disabled={sending || !presets}
                  className={selectClass}
                  title="What they can do (Capture / Build / Knowledge Base)"
                >
                  {!presets && (
                    <option value="" disabled>
                      {presetsError ? 'Presets unavailable' : 'Loading presets…'}
                    </option>
                  )}
                  {(presets ?? []).map((p) => (
                    <option key={p.slug} value={p.slug} title={p.description}>
                      {p.label}
                    </option>
                  ))}
                  <option value="guest" title="External reader — published docs in one space only">
                    Guest (external)
                  </option>
                </select>
                {row.persona === 'guest' ? (
                  <select
                    value={row.space ?? ''}
                    onChange={(e) => {
                      const next = [...composer];
                      next[i] = { ...row, space: e.target.value || undefined };
                      setComposer(next);
                    }}
                    disabled={sending}
                    className={selectClass}
                    title="The one space this guest can read"
                  >
                    <option value="">Choose a space…</option>
                    {(spaces ?? []).map((s) => (
                      <option key={s.spaceId} value={s.spaceId}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={row.role}
                    onChange={(e) => {
                      const next = [...composer];
                      next[i] = { ...row, role: e.target.value as InviteRole };
                      setComposer(next);
                    }}
                    disabled={sending}
                    className={selectClass}
                    title="Workspace administration rank"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => setComposer(composer.filter((_, j) => j !== i))}
                  disabled={sending}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  title="Remove row"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {composerError && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" /> {composerError}
            </p>
          )}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setComposer([
                  ...composer,
                  { email: '', role: 'editor', persona: DEFAULT_INVITE_PRESET },
                ])
              }
              disabled={sending || composer.length >= 25}
              className="flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <Plus className="w-3.5 h-3.5" /> Add email
            </button>
            {composer.some((r) => r.email.trim()) && (
              <button
                type="button"
                onClick={sendInvites}
                disabled={sending || guestRowsIncomplete || !presets}
                title={
                  guestRowsIncomplete
                    ? 'Choose a space for every guest invite'
                    : !presets
                      ? 'Waiting for the access presets'
                      : undefined
                }
                className={cn(
                  'flex items-center gap-2 rounded-md bg-purple-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-purple-700',
                  (sending || guestRowsIncomplete || !presets) && 'opacity-50',
                )}
              >
                {sending ? <Spinner size="sm" tone="current" /> : <Mail className="w-4 h-4" />}
                Send invites
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
